import { useState } from "react";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import { useTranslation } from "next-i18next";
import {
  Hero as SundayHero,
  Headline as SundayHeadline,
  Body as SundayBody,
  Title as SundayTitle,
} from "sunday-component";
import { getPlanMatchedPricingItem } from "src/api/plan";
import { ChannelConfigType } from "src/types/channelConfig";
import { EntryFormInputType } from "src/types/entryForm";
import { PlanType } from "src/types/plan";
import { ProductCategoryType } from "src/types/productCategory";
import { StarterPriceType } from "src/types/starterPrice";
import { checkUnknownError } from "src/utils/errors";
import { logError } from "src/utils/logger";
import { filterParams, formatPathByLogicalGroupChannel } from "src/utils/url";
import styled from "styled-components";
import { color } from "src/styles/globals";
import useBreakpoint from "antd/lib/grid/hooks/useBreakpoint";
import { GLOBAL_PARAMS } from "src/constants/params";
import { DATE_INPUT_TEXT_FORMAT } from "src/constants/format";
import { submitDirectLinkDropLead } from "src/api/droplead";
import { down } from "styled-breakpoints";
import { RETAIL_LOGICAL_GROUP_CHANNEL_CODE } from "src/constants/logicalGroupChannelCode";
import { utmCheckout } from "src/utils/cookies";
import { Banner as BannerType } from "../../types/banner";
import EntryFormBanner from "../EntryForm/EntryFormBanner";
import ProductList from "./ProductList";
import EntryFormModal from "../EntryForm/EntryFormModal";
import PlanContentDirectLinkDetail from "./PlanContentDirectLinkDetail";
import OtherPlanBanner from "./OtherPlanBanner";
import ErrorDialog from "../Common/ErrorDialog";
import NotFoundDirectLinkError from "./NotFoundDirectLinkError/NotFoundDirectLinkError";

const RemarkText = styled(SundayBody)`
  color: ${color.charcoal08};
  text-align: center;
  margin: 0px 0px 40px 0px;
`;

const TitleDesktop = styled(SundayHero)`
  color: ${color.charcoal10};
  text-align: center;
  margin: 40px auto 24px auto;
`;

const TitleMobile = styled(SundayHeadline)`
  color: ${color.charcoal10};
  text-align: center;
  margin: 24px auto;
`;

const Title = styled(SundayTitle)`
  color: ${color.charcoal10};
  text-align: center;
  line-height: 110%;
  margin: 0px 16px;
`;

const TitleSection = styled.div`
  margin: 24px auto;
  padding: 0px 16px;
  text-align: center;
  max-width: 476px;
  ${down("lg")} {
    max-width: 375px;
  }
`;

export type RemarkTextSectionPropType = {
  remark: string;
};

const RemarkTextSection = ({ remark }: RemarkTextSectionPropType) => (
  <RemarkText level={8}>{remark}</RemarkText>
);

export type PlanContentDirectLinkPagePropType = {
  channelPath: string;
  channelConfig: ChannelConfigType;
  plan: PlanType;
  starterPrice: StarterPriceType;
  productCategory: ProductCategoryType;
  banner: BannerType;
  productCategoryActiveList: ProductCategoryType[];
};

const PlanContentDirectLinkPage = ({
  channelPath,
  channelConfig,
  plan,
  starterPrice,
  productCategory,
  banner,
  productCategoryActiveList,
}: PlanContentDirectLinkPagePropType) => {
  const router = useRouter();
  const { locale } = router;
  const { content } = channelConfig;
  const productCategories = productCategoryActiveList
    .map((product) => ({
      ...product,
    }))
    .sort((a, b) => a.seq - b.seq);
  const isRET =
    channelConfig.logicalGroupChannelCode === RETAIL_LOGICAL_GROUP_CHANNEL_CODE;
  const { t } = useTranslation(["plan-content-direct-link", "entry-form"]);
  const { lg } = useBreakpoint();

  const [isPriceLoading, setIsPriceLoading] = useState<boolean>(false);
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isMatchedPricingItem, setIsMatchedPricingItem] =
    useState<boolean>(false);
  const [price, setPrice] = useState<number>(starterPrice.price);
  const [insurerPlanCode, setInsurerPlanCode] = useState<string>(
    starterPrice.insurerPlanCode
  );
  const [entryFormInput, setEntryFormInput] =
    useState<Partial<EntryFormInputType> | null>(null);
  const [isTechnicalErrorDialogVisible, setIsTechnicalErrorDialogVisible] =
    useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const onOpenModal = () => {
    setIsVisible(true);
  };

  const onGoToPlanPage = async () => {
    const entryFormPageUrl = formatPathByLogicalGroupChannel(
      `/${channelPath}/${productCategory.code}/plan`
    );
    const query = {
      ...filterParams(router.query, GLOBAL_PARAMS),
      ...Object.fromEntries(
        Object.entries(entryFormInput).filter(([_, input]) => input ?? false)
      ),
      channelPath,
      categoryCode: productCategory.code,
    };
    await router.push({
      pathname: entryFormPageUrl,
      query,
    });
  };

  const onEntryFormSubmit = async (values: Partial<EntryFormInputType>) => {
    try {
      setIsPriceLoading(true);
      setEntryFormInput(values);

      const matchedPricingItem = await getPlanMatchedPricingItem(
        plan.code,
        productCategory.code,
        channelConfig.code,
        values
      );

      const utmObj = utmCheckout(channelPath);
      const sRefCode = router.query?.sRefCode;

      if (values?.leadRequest) {
        const req = {
          type: "plan-direct-link",
          channelCode: channelConfig.code,
          body: {
            planName: plan.displayName.th,
            productName: plan.product.displayName.th,
            planCode: plan.code,
            productCode: plan.product.code,
            productType: plan.product.productType,
            channelUrl: `${window.location.protocol}//${window.location.hostname}/${channelPath}`,
            contentDirectLinkUrl: `${window.location.protocol}//${window.location.hostname}${window.location.pathname}`,
            price: matchedPricingItem.price,
            fullName: values.fullName,
            categoryCode: productCategory.code,
            mobileNumber: values.mobileNumber,
            sRefCode,
            utmCampaign: utmObj.utmCampaign,
            utmMedium: utmObj.utmMedium,
            utmSource: utmObj.utmSource,
            entryForm: {
              code: productCategory.entryForm?.code,
              input: {
                insuredDateOfBirth: values.insuredDateOfBirth,
                effectiveDate: values.effectiveDate,
              },
            },
          },
        };

        await submitDirectLinkDropLead(req);
      }

      // remove null input value
      const queryString = {
        ...filterParams(router.query, GLOBAL_PARAMS),
        ...Object.fromEntries(
          Object.entries({
            insuredDateOfBirth: values.insuredDateOfBirth,
            effectiveDate: values.effectiveDate,
          }).filter(([_, input]) => input != null)
        ),
      };
      router.push(
        {
          pathname: window.location.pathname,
          query: queryString,
        },
        undefined,
        { shallow: true }
      );

      setPrice(matchedPricingItem.price);
      setInsurerPlanCode(matchedPricingItem.insurerPlanCode);
      setIsMatchedPricingItem(true);
      setIsError(false);
    } catch (error) {
      // send unknown error to sentry
      const isUnknownError = checkUnknownError(error);
      if (isUnknownError) {
        logError(
          "Plan content direct page error by get matched pricing item error",
          {
            planCode: plan.code,
            categoryCode: productCategory.code,
            channelCode: channelConfig.code,
            entryFormInput: values,
          },
          error
        );
        setIsTechnicalErrorDialogVisible(true);
      } else {
        setIsError(true);
        setPrice(starterPrice.price);
        setIsMatchedPricingItem(false);
        const utmObj = utmCheckout(channelPath);
        const sRefCode = router.query?.sRefCode;

        if (values?.leadRequest) {
          const req = {
            type: "plan-direct-link",
            channelCode: channelConfig.code,
            body: {
              planName: plan.displayName.th,
              productName: plan.product.displayName.th,
              planCode: plan.code,
              productCode: plan.product.code,
              productType: plan.product.productType,
              channelUrl: `${window.location.protocol}//${window.location.hostname}/${channelPath}`,
              contentDirectLinkUrl: `${window.location.protocol}//${window.location.hostname}${window.location.pathname}`,
              fullName: values.fullName,
              categoryCode: productCategory.code,
              mobileNumber: values.mobileNumber,
              sRefCode,
              utmCampaign: utmObj.utmCampaign,
              utmMedium: utmObj.utmMedium,
              utmSource: utmObj.utmSource,
              entryForm: {
                code: productCategory.entryForm?.code,
                input: {
                  insuredDateOfBirth: values.insuredDateOfBirth,
                  effectiveDate: values.effectiveDate,
                },
              },
              additionalDetails: "อายุของลูกค้าไม่อยู่ในช่วงอายุของแผนที่เลือก",
            },
          };

          await submitDirectLinkDropLead(req);
        }

        if (typeof window !== "undefined") {
          window.scrollTo({ top: 0, behavior: "auto" });
        }
      }
    } finally {
      setIsPriceLoading(false);

      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "auto" });
      }
    }
  };

  const onGoToAppFormPage = async () => {
    const appFormUrl = formatPathByLogicalGroupChannel(
      `/${channelPath}/${productCategory.code}/plan/${plan.code}/application-form`
    );
    const query = {
      ...router.query,
      ...Object.fromEntries(
        Object.entries(entryFormInput).filter(([_, input]) => input ?? false)
      ),
      channelPath,
      categoryCode: productCategory.code,
    };
    await router.push({ pathname: appFormUrl, query });
  };

  const onGoToEntryFormPage = async () => {
    const entryFormPageUrl = formatPathByLogicalGroupChannel(
      `/${channelPath}/${productCategory.code}`
    );
    const query = filterParams(router.query, GLOBAL_PARAMS);
    await router.push({
      pathname: entryFormPageUrl,
      query,
    });
  };

  const onReloadPage = () => {
    router.reload();
  };

  const techinicalErrorDialogProps = {
    title: t("errorDialog.apiError.title"),
    description: t("errorDialog.apiError.description"),
    errorButtons: [
      {
        text: t("errorDialog.apiError.submitText"),
        onClick: onReloadPage,
      },
    ],
  };

  const initEffectiveDate = router.query.effectiveDate
    ? typeof router.query.effectiveDate === "string" &&
      dayjs(router.query.effectiveDate, DATE_INPUT_TEXT_FORMAT)
    : dayjs().add(1, "days");
  const initInsuredDateOfBirth = router.query.insuredDateOfBirth
    ? typeof router.query.insuredDateOfBirth === "string" &&
      dayjs(router.query.insuredDateOfBirth, DATE_INPUT_TEXT_FORMAT)
    : undefined;

  const initialValuesEntryForm = {
    effectiveDate: initEffectiveDate?.isValid() ? initEffectiveDate : null,
    insuredDateOfBirth: initInsuredDateOfBirth?.isValid()
      ? initInsuredDateOfBirth
      : null,
    insuredGender: router.query.insuredGender as string,
  };

  return (
    <div>
      <ErrorDialog
        {...techinicalErrorDialogProps}
        isVisible={isTechnicalErrorDialogVisible}
      />
      <EntryFormModal
        titleText={
          <TitleSection>
            <Title
              data-testid="entry-form-modal-description"
              data-gtm-input-code="entry-form-modal-description"
              level={7}
            >
              {t("entry-form:form.description", { ns: "entry-form" })}
            </Title>
          </TitleSection>
        }
        visible={isVisible}
        entryFormCode={productCategory.entryForm?.code}
        activeInputCodes={productCategory.entryForm?.activeInputCodes}
        setVisible={setIsVisible}
        onSubmit={onEntryFormSubmit}
        leadEnabled={content.directlink?.plan?.dropLead?.enabled}
        leadDefaultSelected={
          content.directlink?.plan?.dropLead?.defaultSelected
        }
        initialValues={initialValuesEntryForm}
        isInitialValuesValidate={
          !!initialValuesEntryForm.effectiveDate &&
          !!initialValuesEntryForm.insuredDateOfBirth
        }
      />
      {isError ? (
        <NotFoundDirectLinkError
          channelPath={channelPath}
          onOpenEntryForm={onOpenModal}
          onGoToPlanPage={onGoToPlanPage}
        />
      ) : (
        <>
          <EntryFormBanner
            title={banner.title}
            desktopImage={banner.desktopImage}
            mobileImage={banner.mobileImage}
            content={content}
          />
          {lg ? (
            <TitleDesktop level={4}>
              {content.directlink.plan.title[locale]}
            </TitleDesktop>
          ) : (
            <TitleMobile level={3}>
              {content.directlink.plan.title[locale]}
            </TitleMobile>
          )}
          <PlanContentDirectLinkDetail
            insurer={plan.product.insurer}
            product={plan.product}
            plan={plan}
            price={price}
            insurerPlanCode={insurerPlanCode}
            labels={plan.labels || []}
            allCoverageItems={plan.allCoverageItems || []}
            specialBenefitItems={plan.specialBenefitItems || []}
            isPriceLoading={isPriceLoading}
            isMatchedPricingItem={isMatchedPricingItem}
            onOpenEntryForm={onOpenModal}
            onGoToAppForm={onGoToAppFormPage}
            CoverageTablePrimaryColor={
              content.plan?.coverageTable?.primaryColor
            }
            CoverageTableSecondaryColor={
              content.plan?.coverageTable?.secondaryColor
            }
            template={content.coverageDetail?.template}
          />
          <RemarkTextSection
            remark={t("remark", { ns: "plan-content-direct-link" })}
          />
          {content.directlink?.othersInCategory?.enabled && (
            <OtherPlanBanner
              title={content.directlink?.othersInCategory?.title}
              description={content.directlink?.othersInCategory?.content}
              imageUrl={content.directlink?.othersInCategory?.imageUrl}
              bgColor={content.directlink?.othersInCategory?.background.color}
              isFullSize={!content.directlink?.otherCategories?.enabled}
              onSeeOtherPlans={
                isMatchedPricingItem && entryFormInput
                  ? onGoToPlanPage
                  : onGoToEntryFormPage
              }
              titleColor={content.directlink?.othersInCategory?.titleTextColor}
              contentColor={
                content.directlink?.othersInCategory?.contentTextColor
              }
            />
          )}
        </>
      )}
      {content.directlink?.otherCategories?.enabled && (
        <ProductList
          isLoading={false}
          productCategories={productCategories}
          channelPath={channelPath}
          content={content}
          isRET={isRET}
        />
      )}
    </div>
  );
};
export default PlanContentDirectLinkPage;
